import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { Field, Input, Select, Textarea, FormGrid, SectionLabel, SaveMessage, Card, CardHeader, SubmitBtn, EmptyState, RecordMeta, Badge, STATES, AHJ_STAGES, AHJ_STAGE_LABELS, PRIORITY_TAGS, PHASES, THREAT_LEVELS, ORG_ROLES } from './Shared';

const STAGE_VARIANTS = { inbound:'red', reqs:'amber', pilot:'blue', mandate:'green' };
const THREAT_VARIANTS = { low:'gray', medium:'amber', high:'red', critical:'red' };
const ROLE_LABELS = { fire_watch_provider:'Fire watch provider', ahj:'AHJ / fire marshal', property_manager:'Property manager', other:'Other' };

function AHJSection({ profile, hasFullAccess }) {
  const [form, setForm] = useState({ jurisdiction_name:'', state:'', contact_name:'', contact_email:'', pipeline_stage:'inbound', request_date:'', credential_requirements:'', notes:'' });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ show:false, text:'', error:false });
  const [records, setRecords] = useState([]);
  useEffect(() => { load(); }, []);
  async function load() { const { data } = await supabase.from('ahj_pipeline').select('*').order('created_at', { ascending:false }); setRecords(data||[]); }
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  async function save() {
    if (!form.jurisdiction_name) { setMsg({ show:true, text:'Jurisdiction name required.', error:true }); return; }
    setLoading(true);
    await supabase.from('ahj_pipeline').insert({ ...form, submitted_by:profile.id, submitted_by_name:profile.full_name||profile.email });
    if (form.contact_name) await supabase.from('contacts').insert({ full_name:form.contact_name, company:form.jurisdiction_name, email:form.contact_email, state:form.state, contact_type:'ahj', source_entity:'technologies', submitted_by:profile.id, submitted_by_name:profile.full_name||profile.email });
    setLoading(false);
    setMsg({ show:true, text:'AHJ entry saved.', error:false });
    setForm({ jurisdiction_name:'', state:'', contact_name:'', contact_email:'', pipeline_stage:'inbound', request_date:'', credential_requirements:'', notes:'' });
    load();
    setTimeout(() => setMsg({ show:false }), 3000);
  }
  return (
    <Card>
      <CardHeader title="AHJ pipeline" hint={records.length + ' jurisdictions'} />
      <FormGrid>
        <Field label="Jurisdiction name"><Input value={form.jurisdiction_name} onChange={set('jurisdiction_name')} placeholder="City of Tampa Fire Marshal" /></Field>
        <Field label="State"><Select value={form.state} onChange={set('state')} options={STATES} placeholder="Select state" /></Field>
        <Field label="Primary contact"><Input value={form.contact_name} onChange={set('contact_name')} placeholder="Fire Marshal Name" /></Field>
        <Field label="Contact email"><Input type="email" value={form.contact_email} onChange={set('contact_email')} /></Field>
        <Field label="Pipeline stage"><Select value={form.pipeline_stage} onChange={set('pipeline_stage')} options={AHJ_STAGES.map(s => ({ value:s, label:AHJ_STAGE_LABELS[s] }))} /></Field>
        <Field label="JUDGR request date"><Input type="date" value={form.request_date} onChange={set('request_date')} /></Field>
        <Field label="Credential requirements" full><Textarea value={form.credential_requirements} onChange={set('credential_requirements')} placeholder="FWA Level 1 required, 30-min patrol intervals..." /></Field>
      </FormGrid>
      <div className="form-actions"><SubmitBtn onClick={save} loading={loading}>Add to pipeline</SubmitBtn></div>
      <SaveMessage {...msg} />
      <div style={{ marginTop:'16px' }}>
        {!records.length ? <EmptyState text="No AHJ entries yet." /> :
          records.map(r => (
            <div key={r.id} className="list-row">
              <div className="list-row-info">
                <div className="list-row-name">{r.jurisdiction_name}</div>
                <div className="list-row-meta">{r.state} · {r.contact_name||'No contact'}{r.credential_requirements ? ' · ' + r.credential_requirements.slice(0,50) + '…' : ''}</div>
                {hasFullAccess && <RecordMeta submitter={r.submitted_by_name} date={r.created_at} />}
              </div>
              <Badge variant={STAGE_VARIANTS[r.pipeline_stage]}>{AHJ_STAGE_LABELS[r.pipeline_stage]}</Badge>
            </div>
          ))
        }
      </div>
    </Card>
  );
}

function WaitlistSection({ profile, hasFullAccess }) {
  const [form, setForm] = useState({ name:'', email:'', organization:'', org_role:'', state:'', source:'' });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ show:false, text:'', error:false });
  const [records, setRecords] = useState([]);
  useEffect(() => { load(); }, []);
  async function load() { const { data } = await supabase.from('waitlist').select('*').order('created_at', { ascending:false }); setRecords(data||[]); }
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  async function save() {
    if (!form.email) { setMsg({ show:true, text:'Email required.', error:true }); return; }
    setLoading(true);
    await supabase.from('waitlist').insert({ ...form, submitted_by:profile.id, submitted_by_name:profile.full_name||profile.email });
    setLoading(false);
    setMsg({ show:true, text:'Waitlist entry saved.', error:false });
    setForm({ name:'', email:'', organization:'', org_role:'', state:'', source:'' });
    load();
    setTimeout(() => setMsg({ show:false }), 3000);
  }
  return (
    <Card>
      <CardHeader title="JUDGR waitlist" hint={records.length + ' entries'} />
      <FormGrid>
        <Field label="Name"><Input value={form.name} onChange={set('name')} placeholder="Full name" /></Field>
        <Field label="Email"><Input type="email" value={form.email} onChange={set('email')} /></Field>
        <Field label="Organization"><Input value={form.organization} onChange={set('organization')} /></Field>
        <Field label="Role"><Select value={form.org_role} onChange={set('org_role')} options={ORG_ROLES.map(r => ({ value:r, label:ROLE_LABELS[r]||r }))} placeholder="Select role" /></Field>
        <Field label="State"><Select value={form.state} onChange={set('state')} options={STATES} placeholder="Select state" /></Field>
        <Field label="Source"><Input value={form.source} onChange={set('source')} placeholder="How did they find JUDGR?" /></Field>
      </FormGrid>
      <div className="form-actions"><SubmitBtn onClick={save} loading={loading}>Add to waitlist</SubmitBtn></div>
      <SaveMessage {...msg} />
      <div style={{ marginTop:'16px' }}>
        {!records.length ? <EmptyState text="No waitlist entries yet." /> :
          records.slice(0,8).map(r => (
            <div key={r.id} className="list-row">
              <div className="list-row-info">
                <div className="list-row-name">{r.name||r.email}</div>
                <div className="list-row-meta">{r.organization} · {r.state}</div>
              </div>
              {r.org_role && <Badge variant="blue">{ROLE_LABELS[r.org_role]||r.org_role}</Badge>}
            </div>
          ))
        }
      </div>
    </Card>
  );
}

function FeatureSection({ profile, hasFullAccess }) {
  const [form, setForm] = useState({ submitter_org_type:'', description:'', priority_tag:'high', phase_relevance:'phase5' });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ show:false, text:'', error:false });
  const [records, setRecords] = useState([]);
  useEffect(() => { load(); }, []);
  async function load() { const { data } = await supabase.from('feature_requests').select('*').order('created_at', { ascending:false }); setRecords(data||[]); }
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const prioVariants = { critical:'red', high:'amber', nice_to_have:'gray' };
  async function save() {
    if (!form.description) { setMsg({ show:true, text:'Description required.', error:true }); return; }
    setLoading(true);
    await supabase.from('feature_requests').insert({ ...form, submitted_by:profile.id, submitted_by_name:profile.full_name||profile.email });
    setLoading(false);
    setMsg({ show:true, text:'Saved.', error:false });
    setForm({ submitter_org_type:'', description:'', priority_tag:'high', phase_relevance:'phase5' });
    load();
    setTimeout(() => setMsg({ show:false }), 3000);
  }
  return (
    <Card>
      <CardHeader title="Feature requests / feedback" hint={records.length + ' total'} />
      <FormGrid>
        <Field label="Submitter org type"><Input value={form.submitter_org_type} onChange={set('submitter_org_type')} placeholder="e.g. AHJ, property manager..." /></Field>
        <Field label="Phase relevance"><Select value={form.phase_relevance} onChange={set('phase_relevance')} options={PHASES.map(p => ({ value:p, label:p.replace('phase','Phase ').replace('_',' ') }))} /></Field>
        <Field label="Priority"><Select value={form.priority_tag} onChange={set('priority_tag')} options={PRIORITY_TAGS.map(p => ({ value:p, label:p.replace('_',' ') }))} /></Field>
        <Field label="Description" full><Textarea value={form.description} onChange={set('description')} placeholder="Describe the requested feature or feedback..." /></Field>
      </FormGrid>
      <div className="form-actions"><SubmitBtn onClick={save} loading={loading}>Log request</SubmitBtn></div>
      <SaveMessage {...msg} />
      <div style={{ marginTop:'16px' }}>
        {!records.length ? <EmptyState text="No feature requests yet." /> :
          records.slice(0,6).map(r => (
            <div key={r.id} style={{ padding:'10px 0', borderBottom:'1px solid #E2E8F0' }}>
              <div className="tag-row" style={{ marginBottom:'6px' }}>
                <Badge variant={prioVariants[r.priority_tag]||'gray'}>{r.priority_tag}</Badge>
                <Badge variant="blue">{r.phase_relevance}</Badge>
                {r.submitter_org_type && <span style={{ fontSize:'11px', color:'#64748B' }}>{r.submitter_org_type}</span>}
              </div>
              <div style={{ fontSize:'13px', color:'#0F172A' }}>{r.description}</div>
              {hasFullAccess && <RecordMeta submitter={r.submitted_by_name} date={r.created_at} />}
            </div>
          ))
        }
      </div>
    </Card>
  );
}

function CompetitorSection({ profile, hasFullAccess }) {
  const [form, setForm] = useState({ competitor_name:'', encountered_where:'', context:'', threat_level:'medium', state:'' });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ show:false, text:'', error:false });
  const [records, setRecords] = useState([]);
  useEffect(() => { load(); }, []);
  async function load() { const { data } = await supabase.from('competitor_sightings').select('*').order('created_at', { ascending:false }); setRecords(data||[]); }
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  async function save() {
    if (!form.competitor_name) { setMsg({ show:true, text:'Competitor name required.', error:true }); return; }
    setLoading(true);
    await supabase.from('competitor_sightings').insert({ ...form, submitted_by:profile.id, submitted_by_name:profile.full_name||profile.email });
    setLoading(false);
    setMsg({ show:true, text:'Sighting logged.', error:false });
    setForm({ competitor_name:'', encountered_where:'', context:'', threat_level:'medium', state:'' });
    load();
    setTimeout(() => setMsg({ show:false }), 3000);
  }
  return (
    <Card>
      <CardHeader title="Competitor sightings" hint={records.length + ' logged'} />
      <FormGrid>
        <Field label="Competitor name"><Input value={form.competitor_name} onChange={set('competitor_name')} placeholder="Company name" /></Field>
        <Field label="State"><Select value={form.state} onChange={set('state')} options={STATES} placeholder="Select state" /></Field>
        <Field label="Encountered where"><Input value={form.encountered_where} onChange={set('encountered_where')} placeholder="e.g. AHJ meeting, lost bid..." /></Field>
        <Field label="Threat level"><Select value={form.threat_level} onChange={set('threat_level')} options={THREAT_LEVELS.map(t => ({ value:t, label:t.charAt(0).toUpperCase()+t.slice(1) }))} /></Field>
        <Field label="Context" full><Textarea value={form.context} onChange={set('context')} placeholder="What were they doing, what product/service, how did they come up..." /></Field>
      </FormGrid>
      <div className="form-actions"><SubmitBtn onClick={save} loading={loading}>Log sighting</SubmitBtn></div>
      <SaveMessage {...msg} />
      <div style={{ marginTop:'16px' }}>
        {!records.length ? <EmptyState text="No sightings logged yet." /> :
          records.slice(0,6).map(r => (
            <div key={r.id} className="list-row">
              <div className="list-row-info">
                <div className="list-row-name">{r.competitor_name} <span style={{ color:'#94A3B8', fontWeight:'400' }}>· {r.state}</span></div>
                <div className="list-row-meta">{r.encountered_where}</div>
                {hasFullAccess && <RecordMeta submitter={r.submitted_by_name} date={r.created_at} />}
              </div>
              <Badge variant={THREAT_VARIANTS[r.threat_level]||'gray'}>{r.threat_level}</Badge>
            </div>
          ))
        }
      </div>
    </Card>
  );
}

export default function TechForms({ profile, hasFullAccess }) {
  const [tab, setTab] = useState('ahj');
  const tabs = [{ id:'ahj', label:'AHJ pipeline' },{ id:'waitlist', label:'JUDGR waitlist' },{ id:'features', label:'Feature requests' },{ id:'competitors', label:'Competitor sightings' }];
  return (
    <div>
      <div className="tab-bar">
        {tabs.map(t => <button key={t.id} onClick={() => setTab(t.id)} className={"tab-item" + (tab===t.id ? " active" : "")}>{t.label}</button>)}
      </div>
      {tab === 'ahj' && <AHJSection profile={profile} hasFullAccess={hasFullAccess} />}
      {tab === 'waitlist' && <WaitlistSection profile={profile} hasFullAccess={hasFullAccess} />}
      {tab === 'features' && <FeatureSection profile={profile} hasFullAccess={hasFullAccess} />}
      {tab === 'competitors' && <CompetitorSection profile={profile} hasFullAccess={hasFullAccess} />}
    </div>
  );
}
